import { memo } from "react";

import { Pie } from "@nivo/pie";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { GenericObject } from "utility/misc";

// -----------------------------------------------------------------------------

const GaugePie = memo(function GaugePie({
  title,
  data = [],
  loading = false,
  riskScore,
}: GaugePieProps) {
  const hasAtLeastOneDatapoint = data.some(
    (entry: GenericObject) => entry.value > 0
  );

  const CenteredMetric = ({ data, centerX, centerY }: any) => {
    return (
      <>
        <text
          x={centerX}
          y={centerY - 50}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: "50px",
            fontWeight: 700,
          }}
        >
          {riskScore}
        </text>
        <text
          x={centerX}
          y={centerY - 15}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: "16px",
            fontWeight: 500,
            textTransform: "uppercase",
          }}
        >
          {getRankingFromRiskScore(riskScore)}
        </text>
      </>
    );
  };

  //
  return (
    <Card
      sx={{
        width: "100%",
        minWidth: "400px",
        height: "400px",
        overflow: "visible",
      }}
    >
      <CardContent sx={{ padding: "8px 16px" }}>
        <Typography variant="overline">{title}</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {loading ? (
              <>
                &nbsp;
                <CircularProgress size={20} />
              </>
            ) : !hasAtLeastOneDatapoint ? (
              "None"
            ) : null}
          </Typography>
        </Box>
      </CardContent>
      {hasAtLeastOneDatapoint ? (
        <Box sx={{ width: 400, height: 400 }}>
          <Pie
            margin={{ top: -100, right: 0, bottom: 10, left: 60 }}
            colors={{ datum: "data.color" }}
            borderWidth={1}
            borderColor={{
              from: "color",
              modifiers: [["darker", 0.5]],
            }}
            isInteractive={false}
            activeOuterRadiusOffset={4}
            startAngle={-90}
            endAngle={90}
            innerRadius={0.85}
            padAngle={1}
            cornerRadius={1}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            data={data as any}
            layers={["arcs", "arcLabels", "arcLinkLabels", CenteredMetric]}
            height={400}
            width={350}
          />
        </Box>
      ) : null}
    </Card>
  );
});

export default GaugePie;

// -----------------------------------------------------------------------------

const getRankingFromRiskScore = (riskScore: number | undefined) => {
  if (!riskScore) return;

  if (riskScore <= 200) {
    return "low";
  } else if (riskScore <= 400) {
    return "med";
  } else if (riskScore <= 600) {
    return "med-high";
  } else {
    return "high";
  }
};

// -----------------------------------------------------------------------------

interface GaugePieProps {
  title: JSX.Element | string;
  data: GenericObject;
  loading?: boolean;
  riskScore: number | undefined;
}
